/**
 * Custom hook for stateful logic that maintains a custom number of days
 * in a date range (with ISO timestamp strings) using week offsets
 *
 * ex) If it is midnight Tuesday 10/19/2021, `weekOffset` is 0, and `days` is 4,
 *     `startDateTimeRange` will be '2021-10-20T00:00:00Z' (24 hours after the current time)
 *     `endDateTimeRange` will be '2021-10-24T00:00:00Z' (4 days after the current time)
 */

import { useState, useEffect } from 'react';
import { addDays } from 'date-fns';

export default function useWeekOffset(days: number) {
  const today = Date.now();
  const defaultStart = addDays(today, 1).toISOString(); // we don't want users booking sessions less than 24 hours in advance
  const defaultEnd = addDays(today, days).toISOString();
  const [weekOffset, setWeekOffset] = useState<number>(0);
  const [startDateTimeRange, setStartDateTimeRange] = useState<string>(defaultStart);
  const [endDateTimeRange, setEndDateTimeRange] = useState<string>(defaultEnd);

  useEffect(() => {
    const dayOffset = weekOffset * days;
    const newStart = addDays(today, weekOffset === 0 ? 1 : dayOffset).toISOString();
    const newEnd = addDays(defaultEnd, dayOffset).toISOString();
    setStartDateTimeRange(newStart);
    setEndDateTimeRange(newEnd);
  }, [weekOffset, days]);

  return {
    weekOffset,
    setWeekOffset,
    startDateTimeRange,
    setStartDateTimeRange,
    endDateTimeRange,
    setEndDateTimeRange,
  };
}
